import { Routes, Route, Navigate, useParams, useSearchParams } from 'react-router-dom';
import { lazy, Suspense } from 'react';
import { LoadingSpinner } from '@/platform/ui/loading-spinner';
import { RequireProductAccess } from '@/platform/auth/require-product-access';

// Layouts
import { DashboardLayout } from './layouts/dashboard-layout';
import { AdminLayout } from './layouts/admin-layout';
import { ParticipantLayout } from './layouts/participant-layout';
import { WorkshopLayout } from './layouts/workshop-layout';

// Auth pages (eager - critical path)
import LoginPage from '@/app/auth/login/page';
import SignupPage from '@/app/auth/signup/page';
import ForgotPasswordPage from '@/app/auth/forgot-password/page';
import ResetPasswordPage from '@/app/auth/reset-password/page';
import VerifyPage from '@/app/auth/verify/page';
import ResendVerificationPage from '@/app/auth/resend-verification/page';

// Dashboard pages (lazy)
const DashboardPage = lazy(() => import('@/app/(dashboard)/dashboard/page'));
const BillingPage = lazy(() => import('@/app/(dashboard)/dashboard/billing/page'));
const MembersPage = lazy(() => import('@/app/(dashboard)/dashboard/members/page'));
const OrganizationPage = lazy(() => import('@/app/(dashboard)/dashboard/organization/page'));
const SettingsPage = lazy(() => import('@/app/settings/page'));
const OnboardingCompanySetupPage = lazy(() => import('@/app/onboarding/company-setup/page'));

// Product A pages (lazy)
const ProductAPage = lazy(() => import('@/app/(dashboard)/products/productA/page'));
const ProductAStartPage = lazy(() => import('@/app/(dashboard)/products/productA/start/page'));
const ProductAHistoryPage = lazy(() => import('@/app/(dashboard)/products/productA/history/page'));
const ProductAAssessmentPage = lazy(() => import('@/app/(dashboard)/products/productA/assessment/[id]/page'));
const ProductAResultsPage = lazy(() => import('@/app/(dashboard)/products/productA/results/[id]/page'));
const ProductASurveysPage = lazy(() => import('@/app/(dashboard)/products/productA/surveys/page'));
const ProductASurveyDetailPage = lazy(() => import('@/app/(dashboard)/products/productA/surveys/[id]/page'));
const ProductASurveyNewPage = lazy(() => import('@/app/(dashboard)/products/productA/surveys/new/page'));
const ProductASurveyReportPage = lazy(() => import('@/app/(dashboard)/products/productA/surveys/[id]/report/page'));

// Product B pages (lazy)
const ProductBPage = lazy(() => import('@/app/(dashboard)/products/productB/page'));
const ProductBStartPage = lazy(() => import('@/app/(dashboard)/products/productB/start/page'));
const ProductBAssessmentPage = lazy(() => import('@/app/(dashboard)/products/productB/assessment/[id]/page'));
const ProductBResultsPage = lazy(() => import('@/app/(dashboard)/products/productB/results/[id]/page'));
const ProductBSurveyDetailPage = lazy(() => import('@/app/(dashboard)/products/productB/surveys/[id]/page'));
const ProductBSurveyNewPage = lazy(() => import('@/app/(dashboard)/products/productB/surveys/new/page'));
const ProductBSurveyReportPage = lazy(() => import('@/app/(dashboard)/products/productB/surveys/[id]/report/page'));

// Participant pages (lazy)
const ParticipantDashboardPage = lazy(() => import('@/app/(participant)/participant/dashboard/page'));
const ParticipantCompletePage = lazy(() => import('@/app/(participant)/participant/complete/page'));
const ParticipantOrganisationsPage = lazy(() => import('@/app/(participant)/participant/organisations/page'));
const ParticipantSurveyPage = lazy(() => import('@/app/(participant)/survey/[token]/page'));

// Workshop pages (lazy)
const WorkshopJoinPage = lazy(() => import('@/app/(workshop)/join/[roomcode]/page'));
const WorkshopAssessmentPage = lazy(() => import('@/app/(workshop)/join/[roomcode]/assessment/page'));
const WorkshopCompletePage = lazy(() => import('@/app/(workshop)/join/[roomcode]/complete/page'));

// Admin pages (lazy)
const AdminDashboardPage = lazy(() => import('@/app/admin/page'));
const AdminUsersPage = lazy(() => import('@/app/admin/users/page'));
const AdminUserDetailPage = lazy(() => import('@/app/admin/users/[id]/page'));
const AdminCompaniesPage = lazy(() => import('@/app/admin/companies/page'));
const AdminCompanyDetailPage = lazy(() => import('@/app/admin/companies/[id]/page'));
const AdminProductAPage = lazy(() => import('@/app/admin/productA/page'));
const AdminProductAQuestionsPage = lazy(() => import('@/app/admin/productA/questions/page'));
const AdminProductARecommendationsPage = lazy(() => import('@/app/admin/productA/recommendations/page'));
const AdminProductAAssessmentsPage = lazy(() => import('@/app/admin/productA/assessments/page'));
const AdminProductAReportConfigPage = lazy(() => import('@/app/admin/productA/report-config/page'));
const AdminProductADemoDataPage = lazy(() => import('@/app/admin/productA/demo-data/page'));
const AdminProductAEmailLogPage = lazy(() => import('@/app/admin/productA/email-log/page'));
const AdminProductBPage = lazy(() => import('@/app/admin/productB/page'));
const AdminProductBQuestionsPage = lazy(() => import('@/app/admin/productB/questions/page'));
const AdminProductBArchetypesPage = lazy(() => import('@/app/admin/productB/archetypes/page'));
const AdminProductBAssessmentsPage = lazy(() => import('@/app/admin/productB/assessments/page'));
const AdminProductBSessionsPage = lazy(() => import('@/app/admin/productB/sessions/page'));
const AdminProductBSessionWorkshopPage = lazy(() => import('@/app/admin/productB/sessions/[id]/workshop/page'));
const AdminProductBQuestionBanksPage = lazy(() => import('@/app/admin/productB/question-banks/page'));
const AdminProductBQuestionBankDetailPage = lazy(() => import('@/app/admin/productB/question-banks/[id]/questions/page'));
const AdminSettingsPage = lazy(() => import('@/app/admin/settings/page'));
const AdminSettingsDataPage = lazy(() => import('@/app/admin/settings/data/page'));
const AdminSettingsEmailPage = lazy(() => import('@/app/admin/settings/email/page'));
const AdminSettingsEmailLogsPage = lazy(() => import('@/app/admin/settings/email/logs/page'));
const AdminSettingsEmailTemplatesPage = lazy(() => import('@/app/admin/settings/email/templates/page'));
const AdminSettingsIntegrationsPage = lazy(() => import('@/app/admin/settings/integrations/page'));
const AdminSettingsLlmPage = lazy(() => import('@/app/admin/settings/llm/page'));
const AdminSettingsCompletionMessagesPage = lazy(() => import('@/app/admin/settings/completion-messages/page'));
const AdminDesignSystemPage = lazy(() => import('@/app/admin/design-system/page'));
const AdminOnboardingInvitationsPage = lazy(() => import('@/app/admin/onboarding/invitations/page'));
const AdminOnboardingJoinRequestsPage = lazy(() => import('@/app/admin/onboarding/join-requests/page'));
const AdminOnboardingReportsPage = lazy(() => import('@/app/admin/onboarding/reports/page'));

function SuspenseWrapper({ children }: { children: React.ReactNode }) {
  return (
    <Suspense fallback={<div className="flex items-center justify-center min-h-[50vh]"><LoadingSpinner /></div>}>
      {children}
    </Suspense>
  );
}

// app/** pages take Next-style params props; we derive them from the router
// hooks here at the single entry point. Assessment routes skip the product
// access guard when a survey_token is present (public survey links).
// Adapters are defined once at module level so their component identity is
// stable across renders — recreating them per render would remount pages and
// wipe in-progress assessment state.

const ProductARawAssessmentPage = lazy(() => import('@/products/productA/routes/assessment/[id]/page'));
const ProductBRawAssessmentPage = lazy(() => import('@/products/productB/routes/assessment/[id]/page'));

// Dashboard-layer assessment pages self-wrap with RequireProductAccess, but
// survey-token links must skip that check (invited participants may not have
// product access). Render the inner page directly in that case.
function makeAssessmentRoute(
  Page: React.ComponentType<{ params: { id: string } }>,
  RawPage: React.ComponentType<{ params: { id: string } }>,
  productKey: 'productA' | 'productB',
) {
  return function AssessmentRoute() {
    const { id } = useParams<{ id: string }>();
    const [searchParams] = useSearchParams();
    const surveyToken = searchParams.get('survey_token');
    if (!id) return null;
    if (surveyToken) return <RawPage params={{ id }} />;
    return (
      <RequireProductAccess productKey={productKey}>
        <Page params={{ id }} />
      </RequireProductAccess>
    );
  };
}

function makeWithId(Page: React.ComponentType<{ params: { id: string } }>) {
  return function RouteWithId() {
    const { id } = useParams<{ id: string }>();
    return id ? <Page params={{ id }} /> : null;
  };
}


const AdminUserDetailRoute = makeWithId(AdminUserDetailPage);
const AdminCompanyDetailRoute = makeWithId(AdminCompanyDetailPage);
const AdminSessionWorkshopRoute = makeWithId(AdminProductBSessionWorkshopPage);
const ProductAAssessmentRoute = makeAssessmentRoute(ProductAAssessmentPage, ProductARawAssessmentPage, 'productA');
const ProductBAssessmentRoute = makeAssessmentRoute(ProductBAssessmentPage, ProductBRawAssessmentPage, 'productB');
const ProductAResultsRoute = makeWithId(ProductAResultsPage);
const ProductASurveyDetailRoute = makeWithId(ProductASurveyDetailPage);
const ProductASurveyReportRoute = makeWithId(ProductASurveyReportPage);
const ProductBResultsRoute = makeWithId(ProductBResultsPage);
const ProductBSurveyDetailRoute = makeWithId(ProductBSurveyDetailPage);
const ProductBSurveyReportRoute = makeWithId(ProductBSurveyReportPage);

// (assessment routes above use makeAssessmentRoute)

function SurveyTokenRoute() {
  const { token } = useParams<{ token: string }>();
  return token ? <ParticipantSurveyPage params={{ token }} /> : null;
}

function WorkshopJoinRoute() {
  const { roomcode } = useParams<{ roomcode: string }>();
  return roomcode ? <WorkshopJoinPage params={{ roomcode }} /> : null;
}

function WorkshopAssessmentRoute() {
  const { roomcode } = useParams<{ roomcode: string }>();
  return roomcode ? <WorkshopAssessmentPage params={{ roomcode }} /> : null;
}

function WorkshopCompleteRoute() {
  const { roomcode } = useParams<{ roomcode: string }>();
  return roomcode ? <WorkshopCompletePage params={{ roomcode }} /> : null;
}

export function AppRoutes() {
  return (
    <Routes>
      {/* Auth routes - no layout wrapper */}
      <Route path="/auth/login" element={<LoginPage />} />
      <Route path="/auth/signup" element={<SignupPage />} />
      <Route path="/auth/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/auth/reset-password" element={<ResetPasswordPage />} />
      <Route path="/auth/verify" element={<VerifyPage />} />
      <Route path="/auth/resend-verification" element={<ResendVerificationPage />} />

      {/* Dashboard routes */}
      <Route element={<DashboardLayout />}>
        <Route path="/dashboard" element={<SuspenseWrapper><DashboardPage /></SuspenseWrapper>} />
        <Route path="/dashboard/billing" element={<SuspenseWrapper><BillingPage /></SuspenseWrapper>} />
        <Route path="/dashboard/members" element={<SuspenseWrapper><MembersPage /></SuspenseWrapper>} />
        <Route path="/dashboard/organization" element={<SuspenseWrapper><OrganizationPage /></SuspenseWrapper>} />

        {/* Product A */}
        <Route path="/products/productA" element={<SuspenseWrapper><ProductAPage /></SuspenseWrapper>} />
        <Route path="/products/productA/start" element={<SuspenseWrapper><ProductAStartPage /></SuspenseWrapper>} />
        <Route path="/products/productA/history" element={<SuspenseWrapper><ProductAHistoryPage /></SuspenseWrapper>} />
        <Route path="/products/productA/assessment/:id" element={<SuspenseWrapper><ProductAAssessmentRoute /></SuspenseWrapper>} />
        <Route path="/products/productA/results/:id" element={<SuspenseWrapper><ProductAResultsRoute /></SuspenseWrapper>} />
        <Route path="/products/productA/surveys" element={<SuspenseWrapper><ProductASurveysPage /></SuspenseWrapper>} />
        <Route path="/products/productA/surveys/new" element={<SuspenseWrapper><ProductASurveyNewPage /></SuspenseWrapper>} />
        <Route path="/products/productA/surveys/:id" element={<SuspenseWrapper><ProductASurveyDetailRoute /></SuspenseWrapper>} />
        <Route path="/products/productA/surveys/:id/report" element={<SuspenseWrapper><ProductASurveyReportRoute /></SuspenseWrapper>} />

        {/* Product B */}
        <Route path="/products/productB" element={<SuspenseWrapper><ProductBPage /></SuspenseWrapper>} />
        <Route path="/products/productB/start" element={<SuspenseWrapper><ProductBStartPage /></SuspenseWrapper>} />
        <Route path="/products/productB/assessment/:id" element={<SuspenseWrapper><ProductBAssessmentRoute /></SuspenseWrapper>} />
        <Route path="/products/productB/results/:id" element={<SuspenseWrapper><ProductBResultsRoute /></SuspenseWrapper>} />
        <Route path="/products/productB/surveys/new" element={<SuspenseWrapper><ProductBSurveyNewPage /></SuspenseWrapper>} />
        <Route path="/products/productB/surveys/:id" element={<SuspenseWrapper><ProductBSurveyDetailRoute /></SuspenseWrapper>} />
        <Route path="/products/productB/surveys/:id/report" element={<SuspenseWrapper><ProductBSurveyReportRoute /></SuspenseWrapper>} />
      </Route>

      {/* Settings */}
      <Route element={<DashboardLayout />}>
        <Route path="/settings" element={<SuspenseWrapper><SettingsPage /></SuspenseWrapper>} />
      </Route>

      {/* Onboarding */}
      <Route path="/onboarding/company-setup" element={<SuspenseWrapper><OnboardingCompanySetupPage /></SuspenseWrapper>} />

      {/* Participant routes */}
      <Route element={<ParticipantLayout />}>
        <Route path="/participant/dashboard" element={<SuspenseWrapper><ParticipantDashboardPage /></SuspenseWrapper>} />
        <Route path="/participant/complete" element={<SuspenseWrapper><ParticipantCompletePage /></SuspenseWrapper>} />
        <Route path="/participant/organisations" element={<SuspenseWrapper><ParticipantOrganisationsPage /></SuspenseWrapper>} />
      </Route>
      <Route path="/survey/:token" element={<SuspenseWrapper><SurveyTokenRoute /></SuspenseWrapper>} />

      {/* Workshop routes */}
      <Route element={<WorkshopLayout />}>
        <Route path="/join/:roomcode" element={<SuspenseWrapper><WorkshopJoinRoute /></SuspenseWrapper>} />
        <Route path="/join/:roomcode/assessment" element={<SuspenseWrapper><WorkshopAssessmentRoute /></SuspenseWrapper>} />
        <Route path="/join/:roomcode/complete" element={<SuspenseWrapper><WorkshopCompleteRoute /></SuspenseWrapper>} />
      </Route>

      {/* Admin routes */}
      <Route element={<AdminLayout />}>
        <Route path="/admin" element={<SuspenseWrapper><AdminDashboardPage /></SuspenseWrapper>} />
        <Route path="/admin/users" element={<SuspenseWrapper><AdminUsersPage /></SuspenseWrapper>} />
        <Route path="/admin/users/:id" element={<SuspenseWrapper><AdminUserDetailRoute /></SuspenseWrapper>} />
        <Route path="/admin/companies" element={<SuspenseWrapper><AdminCompaniesPage /></SuspenseWrapper>} />
        <Route path="/admin/companies/:id" element={<SuspenseWrapper><AdminCompanyDetailRoute /></SuspenseWrapper>} />

        {/* Admin Product A */}
        <Route path="/admin/productA" element={<SuspenseWrapper><AdminProductAPage /></SuspenseWrapper>} />
        <Route path="/admin/productA/questions" element={<SuspenseWrapper><AdminProductAQuestionsPage /></SuspenseWrapper>} />
        <Route path="/admin/productA/recommendations" element={<SuspenseWrapper><AdminProductARecommendationsPage /></SuspenseWrapper>} />
        <Route path="/admin/productA/assessments" element={<SuspenseWrapper><AdminProductAAssessmentsPage /></SuspenseWrapper>} />
        <Route path="/admin/productA/report-config" element={<SuspenseWrapper><AdminProductAReportConfigPage /></SuspenseWrapper>} />
        <Route path="/admin/productA/demo-data" element={<SuspenseWrapper><AdminProductADemoDataPage /></SuspenseWrapper>} />
        <Route path="/admin/productA/email-log" element={<SuspenseWrapper><AdminProductAEmailLogPage /></SuspenseWrapper>} />

        {/* Admin Product B */}
        <Route path="/admin/productB" element={<SuspenseWrapper><AdminProductBPage /></SuspenseWrapper>} />
        <Route path="/admin/productB/questions" element={<SuspenseWrapper><AdminProductBQuestionsPage /></SuspenseWrapper>} />
        <Route path="/admin/productB/archetypes" element={<SuspenseWrapper><AdminProductBArchetypesPage /></SuspenseWrapper>} />
        <Route path="/admin/productB/assessments" element={<SuspenseWrapper><AdminProductBAssessmentsPage /></SuspenseWrapper>} />
        <Route path="/admin/productB/sessions" element={<SuspenseWrapper><AdminProductBSessionsPage /></SuspenseWrapper>} />
        <Route path="/admin/productB/sessions/:id/workshop" element={<SuspenseWrapper><AdminSessionWorkshopRoute /></SuspenseWrapper>} />
        <Route path="/admin/productB/question-banks" element={<SuspenseWrapper><AdminProductBQuestionBanksPage /></SuspenseWrapper>} />
        <Route path="/admin/productB/question-banks/:id/questions" element={<SuspenseWrapper><AdminProductBQuestionBankDetailPage /></SuspenseWrapper>} />

        {/* Admin Settings */}
        <Route path="/admin/settings" element={<SuspenseWrapper><AdminSettingsPage /></SuspenseWrapper>} />
        <Route path="/admin/settings/data" element={<SuspenseWrapper><AdminSettingsDataPage /></SuspenseWrapper>} />
        <Route path="/admin/settings/email" element={<SuspenseWrapper><AdminSettingsEmailPage /></SuspenseWrapper>} />
        <Route path="/admin/settings/email/logs" element={<SuspenseWrapper><AdminSettingsEmailLogsPage /></SuspenseWrapper>} />
        <Route path="/admin/settings/email/templates" element={<SuspenseWrapper><AdminSettingsEmailTemplatesPage /></SuspenseWrapper>} />
        <Route path="/admin/settings/integrations" element={<SuspenseWrapper><AdminSettingsIntegrationsPage /></SuspenseWrapper>} />
        <Route path="/admin/settings/llm" element={<SuspenseWrapper><AdminSettingsLlmPage /></SuspenseWrapper>} />
        <Route path="/admin/settings/completion-messages" element={<SuspenseWrapper><AdminSettingsCompletionMessagesPage /></SuspenseWrapper>} />

        {/* Admin Design System */}
        <Route path="/admin/design-system" element={<SuspenseWrapper><AdminDesignSystemPage /></SuspenseWrapper>} />

        {/* Admin Onboarding */}
        <Route path="/admin/onboarding/invitations" element={<SuspenseWrapper><AdminOnboardingInvitationsPage /></SuspenseWrapper>} />
        <Route path="/admin/onboarding/join-requests" element={<SuspenseWrapper><AdminOnboardingJoinRequestsPage /></SuspenseWrapper>} />
        <Route path="/admin/onboarding/reports" element={<SuspenseWrapper><AdminOnboardingReportsPage /></SuspenseWrapper>} />
      </Route>

      {/* Root redirect */}
      <Route path="/" element={<Navigate to="/dashboard" replace />} />

      {/* Catch-all */}
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}
